import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { AxiosError } from "axios";
import KalixLogo from "../../components/svgElements/KalixLogo";
import InputWrapper from "../../components/formElements/InputWrapper";
import Textbox from "../../components/formElements/Textbox";
import { Link } from "react-router-dom";
import Button from "../../components/formElements/Button";
import { ROUTES } from "../../constants/routes";
import { forgotPassword } from "../../services/auth";
import {
  EMAIL_REGEX,
  EMAIL_MIN_LENGTH,
  EMAIL_MAX_LENGTH,
} from "../../utils/constant";
import { showErrorToast, showSuccessToast } from "../../utils/helper";
import "./Auth.scss";

interface IForgotPasswordForm {
  email: string;
}

const currentYear = new Date().getFullYear();

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IForgotPasswordForm>({
    mode: "onSubmit",
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: IForgotPasswordForm) => {
    try {
      setIsLoading(true);
      const response = await forgotPassword(data.email);

      if (response.success) {
        showSuccessToast(response.message || t("auth.forgotPasswordSuccess"));
        navigate(ROUTES.VERIFICATION, {
          state: { email: data.email, otpSent: true },
        });
      } else {
        showErrorToast(response.message || t("auth.forgotPasswordFailed"));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        t("auth.forgotPasswordError");
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="userauth-main">
      <div className="row g-3">
        <div className="col-md-7">
          <div className="userauth-bg" />
        </div>

        <div className="col-md-5">
          <div className="userauth-form">
            <div className="userauth-logo">
              <KalixLogo />
            </div>
            <div className="userauth-form-content">
              <h2 className="text-center">{t("auth.forgotPassword")}</h2>
              <p className="text-center">
                {t("auth.forgotPasswordDescription")}
              </p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.email")}
                  </InputWrapper.Label>
                  <Textbox
                    type="email"
                    name="email"
                    placeholder={t("auth.enterEmail")}
                    control={control}
                    className="form-control"
                    rules={{
                      required: t("validation.emailRequired"),
                      minLength: {
                        value: EMAIL_MIN_LENGTH,
                        message: t("validation.emailMinLength", {
                          count: EMAIL_MIN_LENGTH,
                        }),
                      },
                      maxLength: {
                        value: EMAIL_MAX_LENGTH,
                        message: t("validation.emailMaxLength", {
                          count: EMAIL_MAX_LENGTH,
                        }),
                      },
                      pattern: {
                        value: EMAIL_REGEX,
                        message: t("validation.emailInvalid"),
                      },
                    }}
                  />
                  {errors.email && (
                    <span className="auth-msg error">
                      {errors.email.message}
                    </span>
                  )}
                </InputWrapper>

                <Button
                  type="submit"
                  className="primary-btn rounded-md w-100"
                  disabled={isLoading}
                >
                  {isLoading ? t("common.loading") : t("auth.resetPassword")}
                </Button>

                <div className="button-align justify-content-center mt-5">
                  <p className="color-dark">
                    {t("auth.rememberPassword")}{" "}
                    <Link to={ROUTES.LOGIN} className="color-primary">
                      {t("auth.signInLink")}
                    </Link>
                  </p>
                </div>
              </form>
            </div>
            <p className="copyright-text">
              {t("auth.copyright", { year: currentYear })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
